import { z } from 'zod';
import { HexColor, HttpsImageUrl, Id, IsoDate, Slug, Url } from './common';
import { DomainKind, DomainStatus, FontKey, RadiusStyle, TenantStatus, ThemeMode } from './enums';
/* ============================================================================
   Tenants, branding and domains (PLAN §2, §7)
   ========================================================================== */
/** Public, pre-login tenant config served by Host (PLAN §7). */
export const TenantPublicConfigSchema = z.object({
    tenantId: z.string().uuid(),
    slug: z.string(),
    appTitle: z.string(),
    tagline: z.string().nullable(),
    brandColor: HexColor,
    fontKey: z.enum(FontKey),
    logoUrl: HttpsImageUrl.nullable(),
    logoDarkUrl: HttpsImageUrl.nullable(),
    iconUrl: HttpsImageUrl.nullable(),
    missionLanguage: z.boolean(),
    showPoweredBy: z.boolean(),
    /** Corner style + default theme from Branding (applied by the app shell). */
    radius: z.enum(RadiusStyle),
    themeMode: z.enum(ThemeMode),
});
export const BrandingSchema = z.object({
    appTitle: z.string().min(1).max(60),
    tagline: z.string().max(160).nullable(),
    brandColor: HexColor,
    fontKey: z.enum(FontKey),
    logoUrl: HttpsImageUrl.nullable(),
    logoDarkUrl: HttpsImageUrl.nullable(),
    iconUrl: HttpsImageUrl.nullable(),
    themeMode: z.enum(ThemeMode),
    radius: z.enum(RadiusStyle),
    missionLanguage: z.boolean(),
    /** false only when the plan entitlement remove_powered_by_devninjaweb is on AND the owner turned it off */
    showPoweredBy: z.boolean(),
    updatedAt: IsoDate,
});
export const BrandingUpdateSchema = BrandingSchema.omit({ updatedAt: true }).partial();
export const TenantDomainSchema = z.object({
    id: Id,
    hostname: z.string(),
    kind: z.enum(DomainKind),
    status: z.enum(DomainStatus),
    isPrimary: z.boolean(),
    /** DNS records the tenant must add (CNAME/A + TXT verification) */
    records: z.array(z.object({
        type: z.enum(['CNAME', 'A', 'TXT']),
        host: z.string(),
        value: z.string(),
        found: z.boolean(),
    })),
    verifiedAt: IsoDate.nullable(),
    lastCheckedAt: IsoDate.nullable(),
    sslStatus: z.string().nullable(),
    createdAt: IsoDate,
});
/** Hostname rules: no IPs, no our own domains (validated again server-side). */
export const Hostname = z
    .string()
    .trim()
    .toLowerCase()
    .max(253)
    .regex(/^(?=.{1,253}$)(?!-)([a-z0-9-]{1,63}(?<!-)\.)+[a-z]{2,63}$/, 'Enter a domain like tasks.acme.com')
    .refine((h) => !/(^|\.)devninjaweb\.com$/.test(h) && !/(^|\.)onrender\.com$/.test(h), 'Use your own domain');
export const DomainAddSchema = z.object({ hostname: Hostname, kind: z.enum(DomainKind) });
export const DomainInstructionsEmailSchema = z.object({
    emails: z.array(z.string().email()).min(1).max(5),
    note: z.string().max(500).optional(),
});
/** The signed-in user's tenant ("company") summary. */
export const TenantSchema = z.object({
    id: Id,
    slug: Slug,
    name: z.string(),
    status: z.enum(TenantStatus),
    temporaryUrl: Url,
    primaryUrl: Url,
    createdAt: IsoDate,
    pendingDeletion: z.object({ scheduledFor: IsoDate, requestedBy: z.string() }).nullable(),
});
export const TenantUpdateSchema = z.object({
    name: z.string().trim().min(2).max(80).optional(),
    industry: z.string().max(60).optional(),
    teamSize: z.enum(['1-10', '11-50', '51-200', '201+']).optional(),
    timezone: z.string().max(60).optional(),
    dateFormat: z.enum(['DD MMM YYYY', 'DD/MM/YYYY', 'MM/DD/YYYY', 'YYYY-MM-DD']).optional(),
});
export const CompanyDeleteSchema = z.object({ confirmName: z.string(), password: z.string() });
export const DataExportSchema = z.object({
    id: Id,
    status: z.enum(['queued', 'running', 'ready', 'failed', 'expired']),
    downloadUrl: Url.nullable(),
    expiresAt: IsoDate.nullable(),
    createdAt: IsoDate,
    completedAt: IsoDate.nullable(),
});
/**
 * First path segments on the temporary host (missions.devninjaweb.com/<segment>) that can never be a
 * workspace slug. PRODUCT_PATHS are product pages served without a workspace; APP_ROUTE_PATHS are tenant
 * app routes, so a link that lost its slug (…/reset/<token>) can be sent to the right workspace instead of
 * showing "Workspace not found".
 */
export const PRODUCT_PATHS = ['claim', 'offer', 'onboard', 'brand', 'assets', 'api', 'app', 'admin', 'www', 'static', 'v1'];
export const APP_ROUTE_PATHS = [
    'login',
    'logout',
    'signup',
    'forgot',
    'reset',
    'reset-password',
    'invite',
    'confirm-email',
    'verify',
    'inbox',
    'projects',
    'boards',
    'settings',
    'team',
    'reports',
    'clients',
];
export const RESERVED_SLUGS = [...PRODUCT_PATHS, ...APP_ROUTE_PATHS];
//# sourceMappingURL=tenants.js.map